// BL-38 — the dashboard's attention strip. It replaces the Priority queue card,
// which sat empty at the top of Bam's screen most days (BL-37).
//
// ⚠ ABSENT WHEN CLEAR, NEVER ABSENT WHEN UNKNOWN. With nothing to act on it
// renders nothing and costs no layout; with a failed read it says so, because
// an empty strip is the sentence "nothing needs you", which a failed read must
// never produce.
//
// SOS is deliberately not here: its cards already sit at the top of every
// admin page (AppLayout → SosAlerts), directly above this strip.

import { Badge } from "@bednbody/ui/components/badge";
import { ReadFailedCard } from "@bednbody/ui/components/read-failed-card";
import { SurfaceCard } from "@bednbody/ui/components/surface-card";
import { cn } from "@bednbody/ui/lib/utils";
import { ArrowRightIcon, WarningCircleIcon } from "@phosphor-icons/react";
import { Link, useLocation } from "react-router-dom";
import type { AttentionKind, LiveAttentionItem } from "@/features/live-map/live-map.attention";
import { drillInState } from "@/lib/drillIn";
import { timeAgo } from "@/lib/time";

const VISIBLE_ROWS = 3;

const KIND_LABEL: Record<AttentionKind, { one: string; many: string }> = {
  payment: { one: "payment issue", many: "payment issues" },
  no_show: { one: "no-show", many: "no-shows" },
  disconnected: { one: "lost connection", many: "lost connections" },
  late: { one: "running late", many: "running late" },
  no_rider: { one: "no rider", many: "no rider" },
  cash: { one: "hub with cash waiting", many: "hubs with cash waiting" },
  kyc: { one: "ID to review", many: "IDs to review" },
  return_wait: { one: "waiting to go home", many: "waiting to go home" },
};

/** Where a row goes: a booking opens in place (AppLayout's `?booking=`). */
function hrefFor(item: LiveAttentionItem, pathname: string): string {
  switch (item.target.kind) {
    case "booking":
      return `${pathname}?booking=${item.target.bookingId}`;
    case "staff":
      return "/live-map";
    case "page":
      return item.target.href;
  }
}

export function AttentionStrip({
  items,
  failed,
}: {
  items: readonly LiveAttentionItem[];
  failed: boolean;
}) {
  const location = useLocation();

  // With nothing to show, the failure IS the message. With items, they still
  // show — one failed check must not hide a late booking the others found.
  if (failed && items.length === 0) {
    return (
      <ReadFailedCard message="Some of the checks behind the attention strip could not be read. Nothing has been ruled out — check the live map." />
    );
  }
  if (items.length === 0) return null;

  const urgent = items.some((item) => item.tone === "danger");
  const counts = new Map<AttentionKind, { count: number; tone: LiveAttentionItem["tone"] }>();
  for (const item of items) {
    const entry = counts.get(item.kind);
    counts.set(item.kind, {
      count: (entry?.count ?? 0) + 1,
      tone: entry?.tone === "danger" ? "danger" : item.tone,
    });
  }

  return (
    <SurfaceCard
      aria-label="Needs action"
      className={cn("space-y-3 p-4", urgent ? "ring-destructive/30" : "ring-warning/35")}
      role="region"
    >
      <div className="flex flex-wrap items-center gap-2">
        <WarningCircleIcon
          className={cn("size-5 shrink-0", urgent ? "text-destructive" : "text-warning")}
          weight="fill"
        />
        <h2 className="text-sm font-semibold text-foreground">
          {items.length} {items.length === 1 ? "thing needs" : "things need"} action
        </h2>
        <ul className="flex flex-wrap items-center gap-2">
          {[...counts].map(([kind, { count, tone }]) => (
            <li key={kind}>
              <Badge variant={tone === "danger" ? "destructive-soft" : "warning-soft"}>
                {count} {count === 1 ? KIND_LABEL[kind].one : KIND_LABEL[kind].many}
              </Badge>
            </li>
          ))}
        </ul>
        <Link
          className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
          state={drillInState(location.pathname)}
          to="/live-map"
        >
          View all on live map
          <ArrowRightIcon className="size-3" />
        </Link>
      </div>

      <ul className="divide-y divide-border">
        {items.slice(0, VISIBLE_ROWS).map((item) => (
          <li key={item.id}>
            <Link
              className="flex items-center gap-3 py-2 transition-colors hover:bg-card-hover"
              state={drillInState(location.pathname)}
              to={hrefFor(item, location.pathname)}
            >
              <Badge variant={item.tone === "danger" ? "destructive-soft" : "warning-soft"}>
                {KIND_LABEL[item.kind].one}
              </Badge>
              <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                {item.title}
                <span className="text-muted-foreground"> · {item.detail}</span>
              </span>
              {item.at ? (
                <span className="shrink-0 text-xs text-muted-foreground">{timeAgo(item.at)}</span>
              ) : null}
            </Link>
          </li>
        ))}
      </ul>
      {failed ? (
        <p className="text-xs text-destructive">
          Some checks could not be read, so this list may be incomplete.
        </p>
      ) : null}
      {items.length > VISIBLE_ROWS ? (
        <p className="text-xs text-muted-foreground">
          +{items.length - VISIBLE_ROWS} more on the live map
        </p>
      ) : null}
    </SurfaceCard>
  );
}
